import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Avatar,
  InputAdornment,
  Alert,
  Chip,
  Radio,
  RadioGroup,
  FormControl,
  FormControlLabel,
  FormLabel,
} from '@mui/material';
import {
  Close,
  PersonOutlined as PersonIcon,
  LocationOn as LocationIcon,
  StickyNote2 as NoteIcon,
  Phone as PhoneIcon,
  Business as CompanyIcon,
} from '@mui/icons-material';
import { appColors } from '../../Config/Theme';
import { addUnlistedPerson } from '../../Services/IncidentService';
import { IncidentRosterPerson, PersonStatus } from '../../Types/Incident';
import { PersonType } from '../../Types/enums';
import { getPersonTypeLabel } from '../../Utils/personTypeLabels';
import { UI_STATUS_LABELS } from '../../Utils/headcountStatus';

const MAX_NOTES = 200;

const PERSON_TYPE_OPTIONS = [
  PersonType.Visitor,
  PersonType.Contractor,
  PersonType.Employee,
  PersonType.Tenant,
];

interface AddUnlistedPersonDialogProps {
  open: boolean;
  incidentId: string;
  onClose: () => void;
  onPersonAdded: (person?: IncidentRosterPerson, displayName?: string) => void;
  isClosed?: boolean;
}

const AddUnlistedPersonDialog: React.FC<AddUnlistedPersonDialogProps> = ({
  open,
  incidentId,
  onClose,
  onPersonAdded,
  isClosed = false,
}) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [personType, setPersonType] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [locationFound, setLocationFound] = useState('');
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState<PersonStatus>('notAccounted');
  const [firstNameError, setFirstNameError] = useState(false);
  const [lastNameError, setLastNameError] = useState(false);
  const [phoneError, setPhoneError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const firstNameRef = useRef<HTMLInputElement>(null);

  // Autofocus first name field when dialog opens
  useEffect(() => {
    if (open) {
      const t = setTimeout(() => firstNameRef.current?.focus(), 100);
      return () => clearTimeout(t);
    }
  }, [open]);

  const resetForm = () => {
    setFirstName('');
    setLastName('');
    setPhone('');
    setPersonType('');
    setCompanyName('');
    setLocationFound('');
    setNotes('');
    setStatus('notAccounted');
    setFirstNameError(false);
    setLastNameError(false);
    setPhoneError(false);
    setError(null);
    setSaving(false);
  };

  const handleClose = () => {
    if (saving) return;
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    const trimFirst = firstName.trim();
    const trimLast = lastName.trim();
    const trimPhone = phone.trim();
    let hasError = false;

    if (!trimFirst) { setFirstNameError(true); hasError = true; }
    if (!trimLast) { setLastNameError(true); hasError = true; }
    if (!trimPhone) { setPhoneError(true); hasError = true; }
    if (hasError) return;

    setSaving(true);
    setError(null);

    if (isClosed) {
      setError('This incident is closed. New people cannot be added.');
      setSaving(false);
      return;
    }

    const displayName = `${trimFirst} ${trimLast}`;
    try {
      const result = await addUnlistedPerson(incidentId, {
        firstName: trimFirst,
        lastName: trimLast,
        displayName,
        phone: trimPhone || undefined,
        personType: personType || undefined,
        companyName: companyName.trim() || undefined,
        notes: notes.trim() || undefined,
        locationFound: locationFound.trim() || undefined,
        status,
      });
      const addedName = displayName;
      resetForm();
      onPersonAdded(result.person, addedName);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add person.');
      setSaving(false);
    }
  };

  const previewFirst = firstName.trim();
  const previewLast = lastName.trim();
  const previewName = previewLast && previewFirst ? `${previewLast}, ${previewFirst}` : (previewFirst || previewLast);
  const hasPreviewContent = previewFirst.length > 0 || previewLast.length > 0;
  const initials = [previewFirst, previewLast]
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase())
    .slice(0, 2)
    .join('');

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: '16px',
            overflow: 'hidden',
            maxWidth: 720,
            maxHeight: '90vh',
          },
        },
      }}
    >
      {/* ── Header ── */}
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2.5,
          py: 1.5,
          borderBottom: `1px solid ${appColors.border.default}`,
        }}
      >
        <Box sx={{ width: 40 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, color: appColors.text.heading }}>
          Add Person
        </Typography>
        <IconButton onClick={handleClose} disabled={saving} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 2, pb: 1 }}>
        {/* ── Info banner ── */}
        {/* <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
            bgcolor: `${appColors.primary.main}0F`,
            border: `1.5px solid ${appColors.primary.main}40`,
            borderRadius: '10px',
            px: 2,
            py: 1.5,
            mb: 2.5,
          }}
        >
          <InfoIcon sx={{ color: appColors.primary.main, fontSize: 22, mt: 0.15 }} />
          <Typography variant="body2" sx={{ color: appColors.text.heading, fontWeight: 500, lineHeight: 1.5 }}>
            Person will be added as a <strong>Manual Add</strong> with the selected initial status.
          </Typography>
        </Box> */}

        {/* ── Error alert ── */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* ── First Name + Last Name row ── */}
        <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5, mt:1, flexDirection: { xs: 'column', sm: 'row' } }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: appColors.text.heading, mb: 0.5 }}>
              First Name <span style={{ color: appColors.status.error }}>*</span>
            </Typography>
            <TextField
              fullWidth
              placeholder="First name"
              value={firstName}
              onChange={(e) => { setFirstName(e.target.value); if (firstNameError) setFirstNameError(false); }}
              inputRef={firstNameRef}
              error={firstNameError}
              helperText={firstNameError ? 'First name is required.' : undefined}
              disabled={saving}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon sx={{ fontSize: 20, color: appColors.text.placeholder }} />
                    </InputAdornment>
                  ),
                },
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: appColors.surface.card } }}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: appColors.text.heading, mb: 0.5 }}>
              Last Name <span style={{ color: appColors.status.error }}>*</span>
            </Typography>
            <TextField
              fullWidth
              placeholder="Last name"
              value={lastName}
              onChange={(e) => { setLastName(e.target.value); if (lastNameError) setLastNameError(false); }}
              error={lastNameError}
              helperText={lastNameError ? 'Last name is required.' : undefined}
              disabled={saving}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon sx={{ fontSize: 20, color: appColors.text.placeholder }} />
                    </InputAdornment>
                  ),
                },
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: appColors.surface.card } }}
            />
          </Box>
        </Box>

        {/* ── Contact Number + Person Type row ── */}
        <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5, flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: appColors.text.heading, mb: 0.5 }}>
              Contact Number <span style={{ color: appColors.status.error }}>*</span>
            </Typography>
            <TextField
              fullWidth
              placeholder="Phone number"
              value={phone}
              onChange={(e) => { setPhone(e.target.value); if (phoneError) setPhoneError(false); }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && firstName.trim() && lastName.trim() && phone.trim() && !saving) {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
              error={phoneError}
              helperText={phoneError ? 'Contact number is required.' : undefined}
              disabled={saving}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon sx={{ fontSize: 20, color: appColors.text.placeholder }} />
                    </InputAdornment>
                  ),
                },
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: appColors.surface.card } }}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: appColors.text.heading }}>Person Type</Typography>
              <Typography variant="caption" sx={{ color: appColors.text.caption }}>(optional)</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
              {PERSON_TYPE_OPTIONS.map((pt) => {
                const label = getPersonTypeLabel(pt) ?? pt;
                const isActive = personType === pt;
                return (
                  <Chip
                    key={pt}
                    label={label}
                    size="small"
                    onClick={() => setPersonType(isActive ? '' : pt)}
                    disabled={saving}
                    sx={{
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      bgcolor: isActive ? `${appColors.primary.main}14` : appColors.surface.card,
                      border: `1px solid ${isActive ? appColors.primary.main : appColors.border.default}`,
                      color: isActive ? appColors.primary.main : appColors.text.body,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: `${appColors.primary.main}0A` },
                    }}
                  />
                );
              })}
            </Box>
          </Box>
        </Box>

        {/* ── Initial Status radio group ── */}
        <FormControl component="fieldset" sx={{ mb: 1.5, width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <FormLabel
              component="legend"
              sx={{ fontWeight: 600, fontSize: '0.875rem', color: appColors.text.heading, '&.Mui-focused': { color: appColors.text.heading } }}
            >
              Initial Status
            </FormLabel>
            <Typography variant="caption" sx={{ color: appColors.text.caption }}>
              Choose the person's initial headcount status.
            </Typography>
          </Box>
          <RadioGroup
            row
            value={status}
            onChange={(e) => setStatus(e.target.value as PersonStatus)}
            sx={{ gap: { xs: 0, sm: 1 } }}
          >
            {(['notAccounted', 'accounted'] as PersonStatus[]).map((s) => (
              <FormControlLabel
                key={s}
                value={s}
                disabled={saving}
                control={<Radio size="small" />}
                label={UI_STATUS_LABELS[s]}
                sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
              />
            ))}
          </RadioGroup>
        </FormControl>

        {/* ── Company + Location Found row ── */}
        <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5, flexDirection: { xs: 'column', sm: 'row' } }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: appColors.text.heading }}>Company</Typography>
              <Typography variant="caption" sx={{ color: appColors.text.caption }}>(optional)</Typography>
            </Box>
            <TextField
              fullWidth
              placeholder="Company name"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              disabled={saving}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <CompanyIcon sx={{ fontSize: 20, color: appColors.text.placeholder }} />
                    </InputAdornment>
                  ),
                },
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: appColors.surface.card } }}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: appColors.text.heading }}>Location Found</Typography>
              <Typography variant="caption" sx={{ color: appColors.text.caption }}>(optional)</Typography>
            </Box>
            <TextField
              fullWidth
              placeholder="Where the person was found"
              value={locationFound}
              onChange={(e) => setLocationFound(e.target.value)}
              disabled={saving}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationIcon sx={{ fontSize: 20, color: appColors.text.placeholder }} />
                    </InputAdornment>
                  ),
                },
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: appColors.surface.card } }}
            />
          </Box>
        </Box>

        {/* ── Notes field ── */}
        <Typography variant="body2" sx={{ fontWeight: 600, color: appColors.text.heading, mb: 0.5 }}>
          Notes{' '}
          <Typography component="span" variant="caption" sx={{ color: appColors.text.placeholder }}>(optional)</Typography>
        </Typography>
        <Box sx={{ position: 'relative', mb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              border: `1px solid ${appColors.border.default}`,
              borderRadius: '8px',
              bgcolor: appColors.surface.card,
              minHeight: 56,
              '&:hover': { borderColor: appColors.border.hover },
              '&:focus-within': { borderColor: appColors.primary.main, borderWidth: 2 },
              opacity: saving ? 0.6 : 1,
              pointerEvents: saving ? 'none' : 'auto',
            }}
          >
            <Box sx={{ pl: 1.5, pt: 1.5, flexShrink: 0 }}>
              <NoteIcon sx={{ fontSize: 18, color: appColors.text.placeholder }} />
            </Box>
            <textarea
              placeholder="Add note..."
              value={notes}
              onChange={(e) => { if (e.target.value.length <= MAX_NOTES) setNotes(e.target.value); }}
              disabled={saving}
              rows={2}
              style={{
                flex: 1,
                border: 'none',
                outline: 'none',
                resize: 'none',
                padding: '12px 12px 20px 8px',
                fontFamily: 'inherit',
                fontSize: '0.875rem',
                lineHeight: 1.5,
                color: appColors.text.heading,
                backgroundColor: 'transparent',
              }}
            />
          </Box>
          <Typography
            variant="caption"
            sx={{ position: 'absolute', bottom: 4, right: 8, color: appColors.text.placeholder }}
          >
            {notes.length} / {MAX_NOTES}
          </Typography>
        </Box>

        {/* ── Preview card (only when name is entered) ── */}
        {hasPreviewContent && (
        <Box
          sx={{
            bgcolor: appColors.surface.page,
            borderRadius: '12px',
            border: `1px solid ${appColors.border.default}`,
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <Avatar sx={{ width: 40, height: 40, bgcolor: appColors.primary.light, color: appColors.primary.main, fontWeight: 600, fontSize: '0.85rem' }}>
            {initials}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" sx={{ color: appColors.text.caption, fontWeight: 600, display: 'block', letterSpacing: '0.03em', textTransform: 'uppercase', fontSize: '0.6rem' }}>
              Roster Preview
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, color: appColors.text.heading }}>
              {previewName}
            </Typography>
            {(personType || companyName.trim()) && (
              <Typography variant="caption" sx={{ color: appColors.text.caption }}>
                {[getPersonTypeLabel(personType), companyName.trim()].filter(Boolean).join(' \u00B7 ')}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              bgcolor: status === 'accounted' ? appColors.status.successBg
                : status === 'notAccounted' ? '#FFEBEE'
                : appColors.surface.inactive,
              border: `1.5px solid ${status === 'accounted' ? appColors.status.success
                : status === 'notAccounted' ? appColors.status.error
                : appColors.border.default}`,
              borderRadius: '8px',
              px: 1,
              py: 0.25,
            }}
          >
            <Typography variant="caption" sx={{
              fontWeight: 700,
              color: status === 'accounted' ? appColors.status.success
                : status === 'notAccounted' ? appColors.status.error
                : appColors.text.caption,
              whiteSpace: 'nowrap',
            }}>
              {UI_STATUS_LABELS[status]}
            </Typography>
          </Box>
        </Box>
        )}
      </DialogContent>

      {/* ── Footer ── */}
      <DialogActions sx={{ px: 3, py: 1.5, gap: 1.5 }}>
        <Button
          variant="outlined"
          onClick={handleClose}
          disabled={saving}
          sx={{
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 500,
            color: appColors.text.caption,
            borderColor: appColors.border.default,
            minHeight: 44,
            flex: '0 0 auto',
            px: 3,
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving}
          fullWidth
          startIcon={<PersonIcon />}
          sx={{
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 600,
            minHeight: 44,
          }}
        >
          {saving ? 'Adding…' : 'Add Person'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddUnlistedPersonDialog;
